import { FC } from 'react';
import {
  ListTotal,
  TableToolbar,
  TableWrapper,
  Table,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRouteParams } from 'utils/useIdParam';
import { APP, ROUTES } from 'config';
import { logActions, logsFiltersSchema, useLogs } from 'store/logs';
import {
  DateRangeFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import moment from 'moment';
import { useSearchParams } from 'utils/navigation';
import { LogDescription } from 'containers/logs';

export const EmployeeLogs: FC = () => {
  const { t } = useTranslation();
  const { id: employeeId } = useRouteParams(ROUTES.employees.details);

  const { searchParams: filters, navigate } =
    useSearchParams(logsFiltersSchema);

  const { data: logs, isLoading } = useLogs({
    ...filters,
    employees: [employeeId],
  });

  return (
    <TableWrapper>
      <TableToolbar sticky>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap="10px"
        >
          <ListTotal total={logs?.total} />

          <Stack direction="row" alignItems="center" gap="10px">
            <Filters
              value={filters}
              initialValues={logsFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('common.date')}
                    value={value.date}
                    initialValue={initialValues.date}
                    onChange={(v) => setFilter('date', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <DateRangeFilter
                        value={value}
                        onApply={applyFilter}
                        onClear={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('common.action')}
                    value={value.actions}
                    initialValue={initialValues.actions}
                    onChange={(v) => setFilter('actions', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={logActions.map((action) => ({
                          key: action,
                          label: t(`logs.action.${action}`),
                        }))}
                      />
                    )}
                  </FilterButton>
                </>
              )}
            </Filters>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={logs?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'date',
            header: t('common.date'),
            cell: ({ row: { original: log } }) =>
              moment(log.createdAt).format('l LT'),
          },
          {
            id: 'employee',
            header: t('common.done_by'),
            cell: ({ row: { original: log } }) =>
              log.owner ? log.owner.name : '-',
          },
          {
            id: 'action',
            header: t('common.action'),
            cell: ({ row: { original: log } }) => <LogDescription log={log} />,
          },
        ]}
        pagination={
          logs && {
            count: logs.total,
            page: logs.page,
            pageSize: logs.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
